import { clsx } from "@mantine/core";
import { FC } from "react";
import { formatRelativeTime } from "utils";
import { GetClaimsQuery } from "graphql/queries/__generated__/claims";
import ClaimActionButtons from "./ClaimActionButtons";

type ClaimData = NonNullable<NonNullable<GetClaimsQuery['getClaims']>[number]>;

interface ClaimCardProps {
  claim: ClaimData;
  offerTitle?: string;
  status?: string;
}

const ClaimCard: FC<ClaimCardProps> = ({
  claim,
  offerTitle,
  status = "Open",
}) => {
  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      case "settled":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatCurrency = (
    amount: number | null | undefined,
    currency: string | null | undefined
  ) => {
    if (!amount || !currency) return "N/A";
    return `${currency} ${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div
      className={clsx(
        "relative bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 ease-in-out border border-gray-200",
        "hover:shadow-xl hover:border-green-300"
      )}
    >
      <div className="absolute top-3 right-3 z-10">
        <span
          className={clsx(
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm",
            getStatusColor(status)
          )}
        >
          {status}
        </span>
      </div>

      <div className="p-5">
        <div className="mb-4 pr-16">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Claim #{claim.offer_claim_id}
          </h3>
          <p className="text-md text-gray-600 font-bold truncate">
            Insured: {claim.offer?.offer_detail?.insured_by || "N/A"}
          </p>
          <p className="text-sm text-gray-600 truncate">
            Policy Number: {claim.offer?.offer_detail?.policy_number || "N/A"}
          </p>
          {offerTitle && (
            <p className="text-sm text-gray-600 truncate">
              Offer Reference: {offerTitle}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Claim Amount
            </p>
            <p className="font-medium text-gray-700">
              {formatCurrency(claim.claim_amount, claim.currency)}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Claim Share
            </p>
            <p className="font-medium text-gray-700">{claim.claim_share_percentage}%</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Claim Date
            </p>
            <p className="font-medium text-gray-700">{claim.claim_date}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date Created
            </p>
            <p className="font-medium text-gray-700">
              {claim.created_at
                ? formatRelativeTime(new Date(claim.created_at))
                : "N/A"}
            </p>
          </div>
        </div>

        {claim.claim_comment && (
          <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Comment
            </p>
            <p className="text-sm text-gray-700">{stripHtml(claim.claim_comment)}</p>
          </div>
        )}

        <div className="border-t border-gray-200 my-4"></div>

        <div className="action-buttons-container">
          <ClaimActionButtons claim={claim} />
        </div>
      </div>
    </div>
  );
};

export default ClaimCard;
